import { VCPM } from '../models/project.model';
import { VcfSizingOutput } from '../models/vcf-sizing-output.model';
import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';
import type { ProjectSummaryDeps } from '../adapters/project-summary.adapter';
import { getEffectiveWorkloadRecommendation, resolveWorkloadHardwareConfigModes } from '../adapters/sizing-recalculation.adapter';
import { buildWorkloadVmCountLookup, buildClusterSummaries, filterPrimarySiteClusters, isStretchedByRecommendation, aggregateDomainInfra } from './stretched-cluster.transform';

export function withCanonicalSonOwners(project: VCPM.Project, workload: VCPM.Workload | undefined, clusters: PcsSizingEngineResponse.Cluster[]) {
  const entity = project.data.clusterConfigurations?.find(c => c.id === workload?.storageCluster?.id);
  return clusters.map(c => c.clusterType === 'SON' && entity
    ? { ...c, ownerRequirementsId: String(entity.workloadDomainId) } : c);
}

export function projectOwnedStorageClusters(project: VCPM.Project, summary: VcfSizingOutput.Summary, deps: ProjectSummaryDeps): void {
  const recommendations = project.data.output?.recommendation?.workloadDomainsRecommendation?.result?.recommendations ?? [];
  for (const owner of summary.workloadDomains ?? []) {
    owner.ownedStorageClusters = [];
    const ownedRaw: PcsSizingEngineResponse.Cluster[][] = [];
    const ownerInput = project.data.workloadDomains?.find(d => String(d.id) === String(owner.id));
    for (const entity of project.data.clusterConfigurations ?? []) {
      if (String(entity.workloadDomainId) !== String(owner.id)) continue;
      const domains = [...(project.data.workloadDomains ?? [])].sort((a, b) =>
        Number(String(b.id) === String(owner.id)) - Number(String(a.id) === String(owner.id)));
      let found = false;
      for (const domain of domains) {
        for (const input of domain.workloadList ?? []) {
          if (input.storageCluster?.id !== entity.id) continue;
          const recommendation = recommendations.find(r => String(r.requirementsId) === String(domain.id));
          const workload = recommendation?.workloadRecommendations?.find(w => String(w.workloadId) === String(input.id));
          if (!workload) continue;
          const { conMode, otherMode } = resolveWorkloadHardwareConfigModes(input, domain, project.data.clusterConfigurations);
          const effective = getEffectiveWorkloadRecommendation(workload, conMode, otherMode);
          // The canonical workload reference identifies this SON even when the engine mints output IDs.
          const clusters = withCanonicalSonOwners(project, input, effective.clusters.filter(c => c.clusterType === 'SON' &&
            (!c.serverConfiguration?.chassis?.id || c.serverConfiguration.chassis.id === entity.id || c.clusterName === entity.name)));
          if (!clusters.length) continue;
          const stretched = isStretchedByRecommendation(clusters);
          const defaults = workload.profileRecommendations?.find(p => p.isRecommended)?.clusters ?? workload.clusters;
          const cards = buildClusterSummaries(effective, filterPrimarySiteClusters(clusters, stretched), deps.vsanArchType, stretched,
            { currentDomainId: owner.id, workloadVmCounts: buildWorkloadVmCountLookup(project.data.workloadDomains) }, defaults);
          owner.ownedStorageClusters.push(...cards.map(c => ({ ...c, isCrossWorkloadDomainCluster: false,
            canonicalStorageClusterId: entity.id,
            sourceRecommendation: { requirementsId: String(domain.id), workloadId: String(workload.workloadId) } })));
          ownedRaw.push(clusters);
          found = true;
          break;
        }
        if (found) break;
      }
      if (!found) owner.ownedStorageClusters.push({ ...new VcfSizingOutput.ClusterSummary(), id: entity.id,
        name: entity.name, clusterBaseName: entity.name, clusterType: 'SON', clusterRole: 'STORAGE',
        canonicalStorageClusterId: entity.id, sizingUnavailable: true, isCrossWorkloadDomainCluster: false });
    }
    // Recompute from real workloads plus one canonical result per owned SON; consumers exclude foreign SONs.
    const ownRecommendation = recommendations.find(r => String(r.requirementsId) === String(owner.id));
    const ownRaw = (ownRecommendation?.workloadRecommendations ?? []).map(w => {
      const input = ownerInput?.workloadList?.find(i => String(i.id) === String(w.workloadId));
      const modes = resolveWorkloadHardwareConfigModes(input, ownerInput, project.data.clusterConfigurations);
      return withCanonicalSonOwners(project, input, getEffectiveWorkloadRecommendation(w, modes.conMode, modes.otherMode).clusters)
        .filter(c => c.clusterType !== 'SON' || !owner.ownedStorageClusters?.some(s =>
          s.canonicalStorageClusterId === input?.storageCluster?.id));
    });
    if (owner.ownedStorageClusters.length) owner.domainInfra = aggregateDomainInfra([...ownRaw, ...ownedRaw], owner.id,
      clusters => deps.getStorageUsageBreakdown(clusters));
    // Canonical ownership also drives badges on the consumers' existing workload cards.
    for (const [index, workload] of owner.workloads.entries()) {
      const raw = ownRecommendation?.workloadRecommendations?.[index];
      const input = ownerInput?.workloadList?.find(i => String(i.id) === String(raw?.workloadId));
      const entity = project.data.clusterConfigurations?.find(c => c.id === input?.storageCluster?.id);
      if (entity) workload.clusters.filter(c => c.clusterType === 'SON').forEach(c => {
        c.isCrossWorkloadDomainCluster = String(entity.workloadDomainId) !== String(owner.id);
      });
    }
  }
}

